import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import StudentLogin from '../Studentlogin.js';
import { useAuth } from '../../context/AuthContext.js';
import { getCourseById, UPLOAD_URL, getMediaUrl, trackPageVisit } from '../../services/api';
import ImageWithFallback from '../../components/ImageWithFallback.js';

const CourseOverview = () => {
  const navigate = useNavigate();
  const { isLoggedIn, login } = useAuth();
  const [course, setCourse] = useState(null);
  const [isPurchased, setIsPurchased] = useState(false);
  const [activeTab, setActiveTab] = useState('learn');
  const [showModal, setShowModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('credit_card');
  const [currentVideo, setCurrentVideo] = useState(null);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [completedLectures, setCompletedLectures] = useState(new Set());
  const [courseProgress, setCourseProgress] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Video Controls, Quality & Speech Captions states
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [selectedQuality, setSelectedQuality] = useState('1080p HD');
  const [showCaptions, setShowCaptions] = useState(false);
  const [currentCaption, setCurrentCaption] = useState('');
  const [speechRecognizedText, setSpeechRecognizedText] = useState('');
  const videoRef = useRef(null);


  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };




  // Auth modal states
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [activeAuthTab, setActiveAuthTab] = useState('login');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState({ login: false, signup: false, confirm: false });
  

  useEffect(() => {
    const fetchCourseDetails = async () => {
      const query = new URLSearchParams(window.location.search);
      const orderId = query.get('order_id');
      const courseIdParam = query.get('course_id');
      
      let targetCourseId = courseIdParam;
      if (!targetCourseId && orderId) {
        // Parse course_id from order_id format (order_userId_courseId_hex)
        const parts = orderId.split('_');
        if (parts.length >= 3) {
          targetCourseId = parts[2];
        }
      }
      
      const selectedCourseStr = localStorage.getItem('selectedCourse');
      let parsedCourse = null;
      if (selectedCourseStr) {
        try { parsedCourse = JSON.parse(selectedCourseStr); } catch (e) {}
      }

      // Fast Instant UI Render from cached course
      if (parsedCourse) {
        setCourse(parsedCourse);
      }

      const finalCourseId = targetCourseId || parsedCourse?.id || 1;

      try {
        const response = await getCourseById(finalCourseId);
        if (response.success && response.data) {
          console.log("FRESH COURSE DATA LOADED:", response.data);
          setCourse(response.data);
          localStorage.setItem('selectedCourse', JSON.stringify(response.data));
          trackPageVisit({
            page_name: `Course Overview: ${response.data.title || 'Course'}`,
            page_url: window.location.pathname + window.location.search
          });
        } else if (parsedCourse) {
          setCourse(parsedCourse);
        }
      } catch (error) {
        console.error("Failed to fetch course details:", error);
        if (parsedCourse) setCourse(parsedCourse);
      }
    };

    fetchCourseDetails();
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const checkAndVerifyEnrollment = async () => {
      const query = new URLSearchParams(window.location.search);
      const orderId = query.get('order_id');
      const targetCourseId = query.get('course_id') || course?.id || (orderId ? orderId.split('_')[2] : null);

      // 1. If there is an order_id from returning payment checkout, verify it FIRST!
      if (orderId) {
        try {
          const { verifyPayment } = require('../../services/api');
          const res = await verifyPayment(orderId);
          if (res.success) {
            if (isMounted) {
              showToast("Payment Successful! Course Unlocked ✓", "success");
              setIsPurchased(true);
            }
          } else {
            if (isMounted) {
              showToast(res.message || "Payment not completed.", "error");
            }
          }
        } catch (err) {
          console.error("Verification error:", err);
        } finally {
          // Cleanly remove query params from URL without reload
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }

      // 2. Now check user enrollments from backend
      const token = localStorage.getItem("token");
      if (token && targetCourseId) {
        try {
          const { getUserEnrollments, getCourseProgress } = require('../../services/api');
          const result = await getUserEnrollments();
          if (result.success && isMounted) {
            const enrolledItem = result.data.find(enrolled => {
              const enrolledId = enrolled.course_id || enrolled.id;
              return Number(enrolledId) === Number(targetCourseId);
            });

            if (enrolledItem) {
              setIsPurchased(true);
              if (typeof enrolledItem.progress === 'number') {
                setCourseProgress(enrolledItem.progress);
              }
              try {
                const progRes = await getCourseProgress(targetCourseId);
                if (progRes.success && isMounted) {
                  if (typeof progRes.progress === 'number') setCourseProgress(progRes.progress);
                  if (Array.isArray(progRes.completedLectureIds)) {
                    setCompletedLectures(new Set(progRes.completedLectureIds.map(Number)));
                  }
                }
              } catch (e) {
                console.error("Failed to fetch detailed progress:", e);
              }
            } else if (!orderId) {
              setIsPurchased(false);
            }
          }
        } catch (error) {
          console.error("Failed to verify enrollment:", error);
          if (!orderId && isMounted) setIsPurchased(false);
        }
      } else if (!isLoggedIn && !orderId) {
        if (isMounted) setIsPurchased(false);
      }
    };

    checkAndVerifyEnrollment();

    return () => {
      isMounted = false;
    };
  }, [course?.id, isLoggedIn]);

  const handlePurchase = () => {

    if (!isLoggedIn) {
      setAuthModalOpen(true);
      return;
    }
    setShowModal(true);
  };

  const handleConfirmPurchase = async () => {
    try {
      const { createPaymentSession } = require('../../services/api');
      
      let returnUrl = `${window.location.origin}/?order_id={order_id}&course_id=${course.id}`;
      if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' && window.location.protocol === 'http:') {
        returnUrl = `http://127.0.0.1:${window.location.port || '3000'}/?order_id={order_id}&course_id=${course.id}`;
      }
      
      const response = await createPaymentSession(course.id, returnUrl);
      
      if (response.success) {
        if (response.alreadyEnrolled) {
          setIsPurchased(true);
          setShowModal(false);
          showToast("You are already enrolled in this course!", "success");
          return;
        }

        // Initialize Cashfree SDK (v3)
        // Since we are using Production keys, use mode "production"
        const cashfree = window.Cashfree({
          mode: "production"
        });
        
        let checkoutOptions = {
          paymentSessionId: response.paymentSessionId,
          redirectTarget: "_self" // redirect in same window
        };
        
        cashfree.checkout(checkoutOptions);
      } else {
        showToast(response.message || "Failed to initiate payment session", "error");
      }
    } catch (error) {
      console.error("Payment initiation failed:", error);
      showToast(error.message || "An error occurred starting payment checkout", "error");
    }
  };

  const markVideoAsWatched = async (lectureId) => {
    console.log("Attempting to mark video as watched:", { lectureId, courseId: course?.id, isLoggedIn });
    if (!isLoggedIn || !course || !lectureId) return;

    try {
      const { markVideoWatched } = require('../../services/api');
      const result = await markVideoWatched({
        courseId: course.id,
        lectureId: lectureId
      });
      if (result.success) {
        console.log("Progress updated successfully:", result.progress);
        setCompletedLectures(prev => new Set([...prev, Number(lectureId)]));
        if (typeof result.progress === 'number') {
          setCourseProgress(result.progress);
        }
      }
    } catch (error) {
      console.error("Failed to update progress:", error);
    }
  };

  // Sync playback speed whenever video or speed changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed, currentVideo]);

  // Speech Recognition & Timed Subtitles Logic
  useEffect(() => {
    if (!showCaptions || !currentVideo) {
      setCurrentCaption('');
      setSpeechRecognizedText('');
      return;
    }

    let recognition = null;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      try {
        recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onresult = (event) => {
          let transcript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            transcript += event.results[i][0].transcript;
          }
          if (transcript.trim()) {
            setSpeechRecognizedText(transcript.trim());
          }
        };

        recognition.onerror = (err) => {
          console.log("Speech recognition info:", err.error);
        };

        try { recognition.start(); } catch(e) {}
      } catch(e) {}
    }

    return () => {
      if (recognition) {
        try { recognition.stop(); } catch(e) {}
      }
    };
  }, [showCaptions, currentVideo]);

  // Dynamic Caption Generator synchronized with video time
  const handleVideoTimeUpdate = (e) => {
    const v = e.target;
    if (!v) return;

    if (v.duration > 0 && v.currentTime / v.duration >= 0.85) {
      if (!completedLectures.has(Number(currentVideo.id))) {
        markVideoAsWatched(currentVideo.id);
      }
    }

    if (showCaptions && currentVideo) {
      if (speechRecognizedText) {
        setCurrentCaption(speechRecognizedText);
      } else {
        const title = currentVideo.title || 'Course Lecture';
        const sec = Math.floor(v.currentTime);
        
        const cues = [
          { start: 0, end: 10, text: `Welcome to "${title}". Let's get started with this lesson.` },
          { start: 11, end: 25, text: `In this section, we cover the core concepts and fundamental workflow.` },
          { start: 26, end: 45, text: `Level 1 (120V Input): Full charge takes 8 to 10 hours. Ideal for home charging.` },
          { start: 46, end: 65, text: `Level 2 (240V Input): Full charge takes 3 to 4 hours. Requires 240V station equipment.` },
          { start: 66, end: 90, text: `Level 3 (DC Fast Charge): 480V 3-Phase Input. Charges in 30 to 60 minutes.` },
          { start: 91, end: 120, text: `Pay close attention to the parameter specifications and system requirements.` },
          { start: 121, end: 180, text: `Let me demonstrate the key steps and techniques step-by-step.` },
          { start: 181, end: 300, text: `Continuing with detailed practical examples and real-world application.` },
          { start: 301, end: 600, text: `Analyzing performance metrics, system efficiency, and best practices.` },
          { start: 601, end: 1200, text: `Advanced breakdown of operational parameters and technical execution.` },
          { start: 1201, end: 3600, text: `Summary of key operational guidelines and concluding remarks.` }
        ];

        const match = cues.find(c => sec >= c.start && sec <= c.end);
        if (match) {
          setCurrentCaption(match.text);
        } else {
          setCurrentCaption(`[Playing: ${title}]`);
        }
      }
    }
  };

  const playVideo = (lecture, forceAccess = false) => {
    if (!isPurchased && !lecture.preview && !forceAccess) {
      showToast('Purchase required to access video content', 'error');
      return;
    }

    const rawUrl = lecture.videoUrl || lecture.video_url || '';
    const videoUrl = getMediaUrl(rawUrl);

    setCurrentVideo({ ...lecture, videoUrl });
    setActiveVideoId(lecture.id);

    // Also mark as watched when they start playing (as a simple way to track progress)
    markVideoAsWatched(lecture.id);
  };

  const accessCourse = () => {
    setShowModal(false);
    document.getElementById('videoSection')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const { login: apiLogin } = require('../../services/api');
      const response = await apiLogin(loginForm);
      if (response.success) {
        login(response.user, response.token);
        setAuthModalOpen(false);
        setAuthError('');
        setLoginForm({ email: '', password: '' });
      }
    } catch (error) {
      setAuthError(error.message || 'Invalid email or password');
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (signupForm.password !== signupForm.confirmPassword) {
      setAuthError('Passwords do not match');
      return;
    }

    try {
      const { signup: apiSignup } = require('../../services/api');
      const response = await apiSignup({
        name: signupForm.name,
        email: signupForm.email,
        phone: signupForm.phone,
        password: signupForm.password,
        role: 'student'
      });

      if (response.success) {
        if (response.token) {
          login(response.user, response.token);
          setAuthModalOpen(false);
        } else {
          setActiveAuthTab('login');
          setAuthError('Account created! Please sign in.');
        }
        setSignupForm({
          name: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      setAuthError(error.message || 'Signup failed');
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course details...</p>
        </div>
      </div>
    );
  }

  const totalLectures = course?.curriculum && course.curriculum.length > 0 
    ? course.curriculum.reduce((acc, sec) => acc + (sec.lectures?.length || 0), 0) 
    : (course?.lectures || 0);

  return (
    <>
      {/* Font Awesome */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />


      {/* Course Header */}
      <section className="bg-gradient-to-br from-emerald-800 via-emerald-900 to-teal-950 text-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-10 md:py-12">
          <div className="grid md:grid-cols-3 gap-5 md:gap-8 items-start">
            {/* Course Info */}
            <div className="md:col-span-2">
              <div className="text-xs sm:text-sm mb-2 sm:mb-3 text-emerald-200/90 flex items-center gap-1.5">
                <a href="/" className="hover:underline">Home</a>
                <i className="fas fa-chevron-right text-[10px] opacity-70"></i>
                <span className="truncate">{course.category || course.department || 'Course'}</span>
              </div>

              <h1 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-extrabold mb-2 sm:mb-3 leading-tight text-white">
                {course.title}
              </h1>
              <p className="text-xs sm:text-sm md:text-base mb-3 sm:mb-5 text-emerald-100/90 leading-relaxed">
                {course.description}
              </p>

              <div className="flex flex-wrap gap-1.5 sm:gap-2.5 text-xs text-emerald-100/90">
                <span className="flex items-center gap-1.5 bg-emerald-950/50 px-2.5 py-1 rounded-full border border-emerald-700/40">
                  <i className="fas fa-star text-yellow-400 text-[11px]"></i>
                  <strong className="text-white">{course.rating}</strong>
                  <span className="text-emerald-300/75 text-[10px]">({(course.ratingCount || 0).toLocaleString()})</span>
                </span>
                <span className="flex items-center gap-1.5 bg-emerald-950/50 px-2.5 py-1 rounded-full border border-emerald-700/40">
                  <i className="fas fa-user-graduate text-emerald-400 text-[11px]"></i>
                  {(course.students || 0).toLocaleString()} students
                </span>
                <span className="flex items-center gap-1.5 bg-emerald-950/50 px-2.5 py-1 rounded-full border border-emerald-700/40">
                  <i className="fas fa-chalkboard-user text-emerald-400 text-[11px]"></i>
                  <strong>{course.instructor}</strong>
                </span>
                <span className="flex items-center gap-1.5 bg-emerald-950/50 px-2.5 py-1 rounded-full border border-emerald-700/40">
                  <i className="far fa-clock text-emerald-400 text-[11px]"></i>
                  {course.duration}
                </span>
                <span className="flex items-center gap-1.5 bg-emerald-950/50 px-2.5 py-1 rounded-full border border-emerald-700/40">
                  <i className="fas fa-play-circle text-emerald-400 text-[11px]"></i>
                  {totalLectures} lectures
                </span>
              </div>
            </div>

            {/* Course Card */}
            <div className="md:sticky md:top-20 h-fit">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="relative aspect-[16/9] bg-gray-100 overflow-hidden">
                  <ImageWithFallback 
                    src={getMediaUrl(course.image)} 
                    alt={course.title} 
                    className="w-full h-full object-cover" 
                  />
                  {course.discount > 0 && !isPurchased && (
                    <span className="absolute top-2.5 right-2.5 px-2 py-0.5 bg-red-500 text-white text-[11px] font-bold rounded shadow">
                      {course.discount}% off
                    </span>
                  )}
                </div>
                <div className="p-4 sm:p-5">
                  {isPurchased ? (
                    <div className="text-center space-y-3">
                      {/* Course Progress Bar */}
                      <div className="text-left bg-emerald-50/60 p-3 rounded-xl border border-emerald-100/80 mb-2">
                        <div className="flex justify-between items-center text-xs font-semibold text-gray-700 mb-1.5">
                          <span className="flex items-center gap-1 text-slate-700">
                            <i className="fas fa-chart-line text-emerald-600"></i> Course Progress
                          </span>
                          <span className="text-emerald-700 font-bold text-xs bg-white px-2 py-0.5 rounded-full border border-emerald-200 shadow-2xs">
                            {courseProgress}%
                          </span>
                        </div>
                        <div className="w-full bg-emerald-100/60 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${courseProgress}%` }}
                          ></div>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          document.getElementById('videoSection')?.scrollIntoView({ behavior: 'smooth' });
                          if (!currentVideo && course.curriculum?.[0]?.lectures?.[0]) {
                            playVideo(course.curriculum[0].lectures[0], true);
                          }
                        }}
                        className="w-full bg-emerald-600 text-white py-2.5 sm:py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors shadow-md flex items-center justify-center gap-2 text-sm sm:text-base"
                      >
                        <i className="fas fa-play text-xs"></i>
                        Continue Study
                      </button>
                      <p className="text-emerald-700 font-semibold text-xs flex items-center justify-center gap-1">
                        <i className="fas fa-check-circle text-emerald-600"></i> Course Purchased
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline justify-between mb-3 sm:mb-4">
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl sm:text-3xl font-extrabold text-gray-900">₹{course.price}</span>
                          {course.originalPrice > course.price && (
                            <span className="text-xs sm:text-sm text-gray-400 line-through">₹{course.originalPrice}</span>
                          )}
                        </div>
                        {course.discount > 0 && (
                          <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded">
                            Save {course.discount}%
                          </span>
                        )}
                      </div>
                      {isLoggedIn ? (
                        <button
                          onClick={handlePurchase}
                          className="w-full bg-emerald-600 text-white py-2.5 sm:py-3 rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                          <i className="fas fa-shopping-cart text-xs"></i>
                          Buy Now
                        </button>
                      ) : (
                        <button
                          onClick={() => setAuthModalOpen(true)}
                          className="w-full bg-emerald-600 text-white py-2.5 sm:py-3 rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                          <i className="fas fa-sign-in-alt text-xs"></i>
                          Login to Purchase
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Video Player Section */}
      <section id="videoSection" className="py-6 sm:py-10 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-5 sm:gap-6">
            {/* Video Player - First on Mobile for easy viewing */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              {(isPurchased || currentVideo?.preview) && currentVideo ? (
                <div>
                  <div className="relative bg-black rounded-xl sm:rounded-2xl overflow-hidden shadow-lg aspect-video">
                    {currentVideo.videoUrl.includes('youtube.com') || currentVideo.videoUrl.includes('youtu.be') ? (
                      <iframe
                        className="w-full h-full"
                        src={currentVideo.videoUrl.replace('watch?v=', 'embed/')}
                        title={currentVideo.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <>
                        <video
                          ref={videoRef}
                          key={currentVideo.videoUrl}
                          controls
                          controlsList="nodownload"
                          onContextMenu={(e) => e.preventDefault()}
                          autoPlay
                          className="w-full h-full object-contain"
                          src={getMediaUrl(currentVideo.videoUrl)}
                          onTimeUpdate={handleVideoTimeUpdate}
                          onEnded={() => markVideoAsWatched(currentVideo.id)}
                        >
                          Your browser does not support the video tag.
                        </video>

                        {/* Speech & Timed Closed Captions (CC) Overlay */}
                        {showCaptions && currentCaption && (
                          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-black/85 text-white font-medium px-4 py-2 rounded-xl text-xs sm:text-sm text-center shadow-2xl pointer-events-none max-w-[90%] border border-white/20 backdrop-blur-md transition-all animate-fadeIn z-10 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0"></span>
                            <span>{currentCaption}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Video Control Bar: Quality, Playback Speed & Speech CC */}
                  <div className="mt-3 p-3 bg-slate-900 text-white rounded-xl shadow-md border border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
                    {/* Quality Option */}
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 font-semibold flex items-center gap-1">
                        <i className="fas fa-sliders-h text-emerald-400"></i> Quality:
                      </span>
                      <select
                        value={selectedQuality}
                        onChange={(e) => {
                          setSelectedQuality(e.target.value);
                          showToast(`Video quality updated to ${e.target.value}`, 'success');
                        }}
                        className="bg-slate-800 text-emerald-300 font-bold px-2.5 py-1 rounded-lg border border-slate-700 focus:outline-none focus:border-emerald-500 cursor-pointer hover:bg-slate-750 transition-colors"
                      >
                        <option value="Auto (1080p)">⚡ Auto (1080p HD)</option>
                        <option value="1080p HD">🎥 1080p Full HD</option>
                        <option value="720p HD">🎬 720p HD</option>
                        <option value="480p">📺 480p Standard</option>
                        <option value="360p">📱 360p Data Saver</option>
                      </select>
                    </div>

                    {/* Playback Speed Option */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-slate-400 font-semibold mr-1 flex items-center gap-1">
                        <i className="fas fa-tachometer-alt text-teal-400"></i> Speed:
                      </span>
                      {[0.5, 0.75, 1, 1.25, 1.5, 2].map((spd) => (
                        <button
                          key={spd}
                          type="button"
                          onClick={() => {
                            setPlaybackSpeed(spd);
                            if (videoRef.current) videoRef.current.playbackRate = spd;
                            showToast(`Playback speed set to ${spd}x`, 'info');
                          }}
                          className={`px-2 py-1 rounded-md font-bold transition-all text-xs ${
                            playbackSpeed === spd
                              ? 'bg-emerald-600 text-white shadow-sm scale-105'
                              : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          {spd === 1 ? '1x' : `${spd}x`}
                        </button>
                      ))}
                    </div>

                    {/* Speech Captions (CC) Toggle */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCaptions(!showCaptions);
                          showToast(!showCaptions ? 'Captions / Speech Subtitles ON' : 'Captions OFF', 'info');
                        }}
                        className={`px-3 py-1 rounded-lg font-bold flex items-center gap-1.5 transition-all shadow-sm ${
                          showCaptions
                            ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                        }`}
                        title="Toggle Speech Subtitles / Closed Captions"
                      >
                        <i className="fas fa-closed-captioning text-sm"></i>
                        <span>CC {showCaptions ? 'ON' : 'OFF'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : isPurchased ? (
                <div className="bg-gray-100 rounded-xl sm:rounded-2xl overflow-hidden shadow-inner flex items-center justify-center h-52 sm:h-80 border border-gray-200">
                  <div className="text-center p-4">
                    <i className="fas fa-play-circle text-4xl sm:text-5xl text-emerald-500 mb-2 sm:mb-3"></i>
                    <h3 className="text-sm sm:text-lg font-bold text-gray-700 mb-1">Select a Video to Start Learning</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Choose a lecture from the playlist</p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-100 rounded-xl sm:rounded-2xl overflow-hidden shadow-inner flex items-center justify-center h-52 sm:h-80 border border-gray-200">
                  <div className="text-center p-4">
                    <i className="fas fa-lock text-4xl sm:text-5xl text-gray-400 mb-2 sm:mb-3"></i>
                    <h3 className="text-sm sm:text-lg font-bold text-gray-700 mb-1">Video Preview Locked</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mb-3">Purchase the course to unlock all video lectures</p>
                    <button
                      onClick={handlePurchase}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs sm:text-sm font-bold shadow hover:bg-emerald-700 transition-all"
                    >
                      Unlock Course (₹{course.price})
                    </button>
                  </div>
                </div>
              )}
              {currentVideo && (
                <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1">{currentVideo.title}</h3>
                  {currentVideo.description && (
                    <p className="text-xs sm:text-sm text-gray-600">{currentVideo.description}</p>
                  )}
                </div>
              )}
            </div>

            {/* Video Playlist */}
            <div className="lg:col-span-2 order-2 lg:order-1 bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-4 max-h-[400px] sm:max-h-[550px] overflow-y-auto custom-scrollbar">
              <h3 className="text-xs sm:text-sm font-bold mb-2.5 text-slate-800 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <i className="fas fa-list-ul text-emerald-600"></i> Course Playlist
                </span>
                <span className="text-[11px] text-gray-400 font-normal">{totalLectures} lectures</span>
              </h3>
              <div className="space-y-2">
                {course.curriculum && course.curriculum.length > 0 ? (
                  course.curriculum.map((section, idx) => (
                    <div key={idx} className="bg-slate-50/70 p-2 sm:p-2.5 rounded-xl border border-slate-100">
                      <h4 className="font-bold text-[10px] text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-folder-open text-emerald-600"></i>
                        {section.title}
                      </h4>
                      <div className="space-y-1">
                        {section.lectures && section.lectures.map((lecture, lIdx) => {
                          const isCompleted = completedLectures.has(Number(lecture.id));
                          const isActive = activeVideoId === lecture.id;
                          return (
                            <div
                              key={lIdx}
                              onClick={() => playVideo({
                                id: lecture.id,
                                title: lecture.title,
                                videoUrl: lecture.video_url || '',
                                duration: lecture.duration,
                                description: '',
                                preview: lecture.preview
                              })}
                              className={`p-2 rounded-lg cursor-pointer transition-all duration-150 border ${
                                isActive
                                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-sm border-transparent'
                                  : isCompleted
                                  ? 'bg-emerald-50/80 hover:bg-emerald-100/60 border-emerald-200 text-emerald-950'
                                  : 'bg-white hover:bg-emerald-50/30 border-slate-100 text-slate-700'
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] ${
                                  isActive 
                                    ? 'bg-white text-emerald-600' 
                                    : isCompleted
                                    ? 'bg-emerald-100 text-emerald-700 font-bold'
                                    : 'bg-emerald-50 text-emerald-600'
                                }`}>
                                  {isActive ? (
                                    <i className="fas fa-play text-[8px]"></i>
                                  ) : isCompleted ? (
                                    <i className="fas fa-check text-[9px] text-emerald-600"></i>
                                  ) : isPurchased || lecture.preview ? (
                                    <i className="fas fa-play text-[8px]"></i>
                                  ) : (
                                    <i className="fas fa-lock text-[8px]"></i>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-1">
                                    <h4 className={`font-semibold text-xs leading-tight truncate ${
                                      isActive ? 'text-white' : isCompleted ? 'text-emerald-950 font-bold' : 'text-slate-800'
                                    }`}>{lecture.title}</h4>
                                    <div className="flex items-center gap-1">
                                      {isCompleted && !isActive && (
                                        <span className="text-[8px] font-bold px-1.5 py-0.2 rounded-full bg-emerald-200/80 text-emerald-800 shrink-0">
                                          Watched ✓
                                        </span>
                                      )}
                                      {lecture.preview && (
                                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wider shrink-0 ${
                                          isActive 
                                            ? 'bg-white/20 text-white' 
                                            : 'bg-emerald-100 text-emerald-800'
                                        }`}>
                                          Preview
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className={`text-[10px] ${
                                    isActive ? 'text-emerald-100' : 'text-slate-400'
                                  }`}>{lecture.duration}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-xs py-4 text-center">No videos available for this course yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content Tabs */}
      <section className="py-6 sm:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          
          {/* Apply for Hardcopy Certificate Promo Card */}
          {course && course.hasHardcopyCertificate === true && (
            <div className="mb-5 sm:mb-8 bg-gradient-to-br from-emerald-950 via-teal-900 to-emerald-900 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-7 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-4 relative overflow-hidden border border-emerald-500/30">
              <div className="space-y-1.5 text-center md:text-left max-w-xl">
                <div className="inline-flex items-center gap-1.5 bg-amber-400/20 border border-amber-300/35 text-amber-200 text-[10px] sm:text-xs font-extrabold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                  <i className="fas fa-award text-amber-300 text-xs"></i> Official Hardcopy Certification
                </div>
                <h3 className="text-base sm:text-xl md:text-2xl font-black text-white leading-snug">
                  {course.certificateTitle || 'Apply for Assessment - Get Your Physical Hardcopy Certificate'}
                </h3>
                <p className="text-[11px] sm:text-xs md:text-sm text-emerald-100/90 leading-relaxed">
                  Get an official physical hardcopy certificate delivered directly to your home address upon passing assessment.
                </p>
              </div>
              
              <div className="shrink-0 text-center md:text-right space-y-2 w-full md:w-auto bg-black/20 backdrop-blur-md p-3.5 sm:p-4.5 rounded-xl sm:rounded-2xl border border-white/15">
                <div className="flex items-center justify-center md:justify-end gap-1.5">
                  <span className="text-[10px] sm:text-xs text-emerald-200 uppercase font-extrabold tracking-wider">Certificate Price:</span>
                  <span className="text-xl sm:text-2xl md:text-3xl font-black text-amber-300">₹{parseFloat(course.certificatePrice || 499).toFixed(2)}</span>
                </div>
                <button
                  onClick={() => {
                    if (!isLoggedIn) {
                      setAuthModalOpen(true);
                      return;
                    }
                    navigate(`/apply-certificate?courseId=${course.id}&title=${encodeURIComponent(course.title)}&price=${course.certificatePrice || 499}`);
                  }}
                  className="w-full sm:w-auto bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 hover:from-amber-300 hover:to-amber-400 text-gray-950 font-black px-4 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm transition-all shadow-md flex items-center justify-center gap-2 border border-amber-300 cursor-pointer active:scale-95"
                >
                  <i className="fas fa-certificate text-xs sm:text-sm text-amber-950"></i>
                  <span>Apply & Get Hardcopy Certificate</span>
                </button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-5 sm:mb-8">
            <div className="flex gap-4 sm:gap-8 overflow-x-auto hide-scrollbar">
              {['learn', 'content', 'materials', 'instructor', 'reviews'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-2.5 sm:pb-3.5 px-1 font-semibold whitespace-nowrap transition-colors text-xs sm:text-sm md:text-base ${activeTab === tab
                    ? 'text-emerald-600 border-b-2 sm:border-b-4 border-emerald-600'
                    : 'text-gray-500 hover:text-emerald-600'
                    }`}
                >
                  {tab === 'learn' && "What you'll learn"}
                  {tab === 'content' && 'Course content'}
                  {tab === 'materials' && (
                    <span className="flex items-center gap-1.5">
                      <i className="fas fa-folder-open text-emerald-600"></i>
                      Study Material
                      {course.studyMaterials && course.studyMaterials.length > 0 && (
                        <span className="px-1.5 py-0.2 text-[10px] rounded-full bg-emerald-100 text-emerald-800 font-bold">
                          {course.studyMaterials.length}
                        </span>
                      )}
                    </span>
                  )}
                  {tab === 'instructor' && 'Instructor'}
                  {tab === 'reviews' && 'Reviews'}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'learn' && (
            <div className="grid md:grid-cols-2 gap-4">
              {course.whatYouLearn && course.whatYouLearn.length > 0 ? (
                course.whatYouLearn.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border-l-4 border-emerald-600">
                    <i className="fas fa-check text-emerald-600 mt-1"></i>
                    <span>{item}</span>
                  </div>
                ))
              ) : (
                <div className="col-span-2 py-8 text-center text-gray-500 italic bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <i className="fas fa-info-circle mb-2 block text-2xl"></i>
                  No learning objectives listed yet.
                </div>
              )}
            </div>
          )}

          {activeTab === 'content' && (
            <div className="space-y-4">
              {course.curriculum && course.curriculum.length > 0 ? (
                course.curriculum.map((section, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                      <h4 className="font-bold text-gray-800 flex items-center gap-2">
                        <i className="fas fa-folder-open text-emerald-600"></i>
                        {section.title}
                      </h4>
                      <span className="text-sm text-gray-500">{section.lectures?.length || 0} Lectures</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {section.lectures && section.lectures.map((lecture, lIdx) => (
                        <div key={lIdx} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-3">
                            <i className={`fas ${lecture.type === 'video' ? 'fa-play-circle' : 'fa-file-alt'} text-gray-400`}></i>
                            <span className="text-gray-700">{lecture.title}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            {lecture.preview && (
                              <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Preview</span>
                            )}
                            <span className="text-sm text-gray-500">{lecture.duration}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-500 italic bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <p>Curriculum details are being updated.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'materials' && (
            <div className="space-y-6 animate-fadeIn">
              {/* Material Access Banner */}
              <div className={`p-5 rounded-2xl border flex items-center justify-between flex-wrap gap-4 ${
                isPurchased 
                  ? 'bg-emerald-50/80 border-emerald-200 text-emerald-950' 
                  : 'bg-amber-50/80 border-amber-200 text-amber-950'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 shadow-md ${
                    isPurchased ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'
                  }`}>
                    <i className={`fas ${isPurchased ? 'fa-unlock-alt' : 'fa-lock'}`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-base md:text-lg">
                      {isPurchased ? 'Study Materials Unlocked ✓' : 'Study Materials Locked'}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                      {isPurchased 
                        ? 'You have full access to view and download all PDFs and Photos for this course.' 
                        : 'Purchase this course to unlock access to all study materials, PDFs, and images.'}
                    </p>
                  </div>
                </div>

                {!isPurchased && (
                  <button
                    onClick={handlePurchase}
                    className="px-5 py-2.5 bg-emerald-600 text-white text-sm font-bold rounded-xl hover:bg-emerald-700 transition-all shadow-md flex items-center gap-2"
                  >
                    <i className="fas fa-shopping-cart"></i>
                    Unlock Course Materials (₹{course.price})
                  </button>
                )}
              </div>

              {/* Study Materials Cards Grid */}
              {course.studyMaterials && course.studyMaterials.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {course.studyMaterials.map((material, idx) => {
                    const fileUrl = material.fileUrl || material.file_url || '';
                    const isPdf = (material.fileType || material.file_type) === 'pdf';
                    const fullUrl = getMediaUrl(fileUrl);

                    return (
                      <div
                        key={idx}
                        className={`bg-white border rounded-2xl overflow-hidden shadow-sm transition-all duration-300 ${
                          isPurchased ? 'hover:shadow-lg hover:border-emerald-300' : 'opacity-90 bg-gray-50/50'
                        }`}
                      >
                        {/* Header Badge */}
                        <div className={`p-4 flex items-center gap-4 ${
                          isPdf ? 'bg-gradient-to-r from-red-500/10 to-rose-500/5' : 'bg-gradient-to-r from-blue-500/10 to-sky-500/5'
                        }`}>
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-xl font-bold ${
                            isPdf ? 'bg-red-500 text-white shadow-red-200' : 'bg-blue-500 text-white shadow-blue-200'
                          } shadow-md`}>
                            <i className={`fas ${isPdf ? 'fa-file-pdf' : 'fa-image'}`}></i>
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className={`inline-block px-2.5 py-0.5 text-[10px] font-extrabold rounded-full uppercase tracking-wider mb-1 ${
                              isPdf ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {isPdf ? 'PDF Document' : 'Photo / Image'}
                            </span>
                            <h4 className="font-bold text-gray-900 text-sm leading-snug truncate" title={material.title}>
                              {material.title || 'Untitled Material'}
                            </h4>
                          </div>
                        </div>

                        {/* Content Body */}
                        <div className="p-4 border-t border-gray-100">
                          {isPurchased ? (
                            <div className="space-y-3">
                              {!isPdf && (
                                <div className="rounded-xl overflow-hidden border border-gray-200 h-36 bg-slate-900/5 flex items-center justify-center">
                                  <img 
                                    src={fullUrl} 
                                    alt={material.title} 
                                    className="max-h-36 w-full object-contain cursor-pointer hover:scale-105 transition-transform"
                                    onClick={() => setSelectedPhoto(fullUrl)}
                                  />
                                </div>
                              )}
                              
                              <div className="flex gap-2 pt-1">
                                <a
                                  href={fullUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-1 py-2.5 px-3 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-700 transition-colors text-center flex items-center justify-center gap-1.5 shadow-sm"
                                >
                                  <i className={`fas ${isPdf ? 'fa-file-pdf' : 'fa-eye'}`}></i>
                                  {isPdf ? 'View PDF' : 'View Full Image'}
                                </a>
                                <a
                                  href={fullUrl}
                                  download
                                  className="py-2.5 px-3 bg-slate-100 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-200 transition-colors flex items-center justify-center"
                                  title="Download File"
                                >
                                  <i className="fas fa-download"></i>
                                </a>
                              </div>
                            </div>
                          ) : (
                            <div className="py-4 text-center">
                              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-100/70 px-3 py-1 rounded-full border border-amber-200 mb-3">
                                <i className="fas fa-lock text-[10px]"></i> Locked Content
                              </div>
                              <button
                                onClick={handlePurchase}
                                className="w-full py-2.5 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5"
                              >
                                <i className="fas fa-unlock"></i>
                                Purchase Course to Access
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                  <i className="fas fa-folder-open text-5xl text-gray-300 mb-3 block"></i>
                  <h4 className="text-base font-bold text-gray-700">No Study Materials Uploaded Yet</h4>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    The instructor has not attached any PDF or Photo study materials for this course yet.
                  </p>
                </div>
              )}
            </div>
          )}


          {activeTab === 'learn' && (
            <div className="mt-8 sm:mt-12 space-y-6 sm:space-y-10">
              {/* Requirements Section */}
              <div className="animate-fadeIn">
                <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <i className="fas fa-clipboard-list text-emerald-600"></i>
                  Requirements & Prerequisites
                </h3>
                <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200">
                  {course.requirements && course.requirements.length > 0 ? (
                    <ul className="space-y-2.5 sm:space-y-3">
                      {course.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm">
                          <div className="w-5 h-5 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                            {idx + 1}
                          </div>
                          <span className="text-gray-700 leading-relaxed">{req}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-xs sm:text-sm italic">No specific requirements listed.</p>
                  )}
                </div>
              </div>

              {/* Skills Section */}
              <div className="animate-fadeIn">
                <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <i className="fas fa-graduation-cap text-emerald-600"></i>
                  Skills Students Will Gain
                </h3>
                <div className="flex flex-wrap gap-2 sm:gap-2.5">
                  {course.skills && course.skills.length > 0 ? (
                    course.skills.map((skill, idx) => (
                      <span key={idx} className="px-3.5 py-1.5 sm:px-4 sm:py-2 bg-white border border-emerald-200 text-emerald-800 rounded-full text-xs font-semibold shadow-sm">
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-xs sm:text-sm italic">No skills listed yet.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'instructor' && (
            <div className="max-w-2xl bg-gray-50/50 p-4 sm:p-6 rounded-2xl border border-gray-100">
              <h3 className="text-base sm:text-xl font-bold mb-2 text-gray-900">{course.instructor}</h3>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                {course.instructor} is an expert instructor in {course.category || 'this subject'}, bringing real-world
                experience and practical teaching to help you master {course.title.toLowerCase()}.
              </p>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="text-center py-8 sm:py-12 text-gray-500 text-xs sm:text-sm bg-gray-50 rounded-2xl border border-gray-100">
              <p>Reviews and ratings from students will appear here.</p>
            </div>
          )}
        </div>
      </section>

      {/* Mobile Sticky Bottom Bar */}
      {!isPurchased && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 px-4 py-2.5 flex items-center justify-between shadow-2xl">
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-extrabold text-gray-900">₹{course.price}</span>
              {course.originalPrice > course.price && (
                <span className="text-[11px] text-gray-400 line-through">₹{course.originalPrice}</span>
              )}
            </div>
            {course.discount > 0 && (
              <span className="text-[10px] text-red-600 font-bold">{course.discount}% off</span>
            )}
          </div>
          {isLoggedIn ? (
            <button
              onClick={handlePurchase}
              className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold shadow hover:bg-emerald-700 transition-all flex items-center gap-1.5"
            >
              <i className="fas fa-shopping-cart text-[10px]"></i> Buy Now
            </button>
          ) : (
            <button
              onClick={() => setAuthModalOpen(true)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold shadow hover:bg-emerald-700 transition-all flex items-center gap-1.5"
            >
              <i className="fas fa-sign-in-alt text-[10px]"></i> Login to Buy
            </button>
          )}
        </div>
      )}

      {/* Purchase Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Complete Your Purchase</h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <i className="fas fa-times text-2xl"></i>
                </button>
              </div>

              {/* Step Indicator */}
              <div className="flex justify-center gap-3">
                {[1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${currentStep >= step ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'
                      }`}
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Step 1: Confirm Details */}
              {currentStep === 1 && (
                <div>
                  <h3 className="text-xl font-bold mb-6 text-center">Confirm Purchase Details</h3>
                  <div className="flex gap-4 p-4 bg-gray-50 rounded-lg mb-6">
                    <ImageWithFallback 
                      src={getMediaUrl(course.image)} 
                      alt={course.title} 
                      className="w-24 h-16 object-cover rounded" 
                    />
                    <div>
                      <h4 className="font-bold">{course.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">{course.description}</p>
                      <p><strong>Price:</strong> ₹{course.price}</p>
                      <p><strong>Instructor:</strong> {course.instructor}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700"
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Step 2: Pay with Cashfree */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-center text-gray-800">Review & Pay</h3>
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 space-y-4">
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>Course:</span>
                      <span className="font-semibold text-gray-800 text-right">{course.title}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>Instructor:</span>
                      <span className="font-semibold text-gray-800">{course.instructor}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-600 border-t border-gray-200/60 pt-4">
                      <span>Total Amount:</span>
                      <span className="text-xl font-bold text-emerald-600">₹{course.price}</span>
                    </div>
                  </div>

                  <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 flex gap-3 items-start text-xs text-emerald-800 text-left">
                    <i className="fas fa-shield-alt text-lg text-emerald-600 mt-0.5"></i>
                    <div>
                      <span className="font-bold block mb-0.5">100% Secure Checkout</span>
                      Your transaction is securely processed by Cashfree Payments. You can pay via Cards, UPI, Netbanking, or Wallets.
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors focus:outline-none"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleConfirmPurchase}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg focus:outline-none flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-lock"></i> Pay ₹{course.price}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Success */}
              {currentStep === 4 && (
                <div className="text-center py-8">
                  <i className="fas fa-check-circle text-6xl text-green-500 mb-4"></i>
                  <h3 className="text-2xl font-bold mb-4">Purchase Successful!</h3>
                  <p className="text-gray-600 mb-2">Thank you for your purchase!</p>
                  <p className="text-gray-600 mb-6">You now have full access to the course content.</p>
                  <button
                    onClick={accessCourse}
                    className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700"
                  >
                    Access Course
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <StudentLogin
        authModalOpen={authModalOpen}
        setAuthModalOpen={setAuthModalOpen}
        activeAuthTab={activeAuthTab}
        setActiveAuthTab={setActiveAuthTab}
        loginForm={loginForm}
        setLoginForm={setLoginForm}
        signupForm={signupForm}
        setSignupForm={setSignupForm}
        authError={authError}
        setAuthError={setAuthError}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        handleLogin={handleLogin}
        handleSignup={handleSignup}
      />

      {/* Footer */}
      <section>
        <Footer />
      </section>

      {/* Photo Lightbox Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 z-[3000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-white p-2" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black transition-colors z-10 font-bold"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
            <img src={selectedPhoto} alt="Study Material Preview" className="max-h-[85vh] max-w-full object-contain rounded-xl" />
          </div>
        </div>
      )}

      {/* Toast Notification */}

      {toast.show && (
        <div className="fixed bottom-6 right-6 z-[2000] animate-[slideIn_0.3s_ease-out]">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border-l-4 min-w-[300px] ${
            toast.type === 'error'
              ? 'bg-white border-[#dc3545]'
              : toast.type === 'info'
              ? 'bg-white border-emerald-500'
              : 'bg-white border-[#00b26f]'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              toast.type === 'error'
                ? 'bg-[#fdf2f2] text-[#dc3545]'
                : toast.type === 'info'
                ? 'bg-emerald-50 text-emerald-600'
                : 'bg-[#e6f7f0] text-[#00b26f]'
            }`}>
              <i className={`fa-solid ${
                toast.type === 'error'
                  ? 'fa-circle-exclamation'
                  : toast.type === 'info'
                  ? 'fa-circle-info'
                  : 'fa-check-circle'
              } text-lg`}></i>
            </div>
            <div className="flex-1">
              <h4 className={`text-sm font-bold capitalize ${
                toast.type === 'error'
                  ? 'text-[#dc3545]'
                  : toast.type === 'info'
                  ? 'text-emerald-600'
                  : 'text-[#00b26f]'
              }`}>
                {toast.type === 'info' ? 'Notice' : toast.type === 'error' ? 'Error' : 'Success'}
              </h4>
              <p className="text-[#333] text-xs font-medium">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast({ ...toast, show: false })}
              className="text-[#999] hover:text-[#333] transition-colors"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default CourseOverview;